// Global packages and components
import { clsx } from 'clsx'
import { Link } from '@remix-run/react'
import { ContentEditor, Button, Video, Picture } from '~/components'
import { getLinkType } from '~/utils'

// Types
import type { CardColumnProps } from '~/types'

interface Props extends CardColumnProps {
  reduceContentWidth?: boolean
}

// Inner content
const BannerInner = ({
  mediaType,
  content,
  image,
  mobileImage,
  video,
  mobileVideo,
  button,
  buttonType,
  alignment,
  reduceContentWidth,
  imageOverlay = true,
  oembed,
  mobileOembed,
}: Props) => (
  <>
    <div
      className={clsx(
        'relative z-[2] flex size-full flex-col px-5 py-8 xl:p-8',
        {
          'justify-start': alignment == 'top',
          'justify-center': alignment == 'middle',
          'justify-end': alignment == 'bottom',
          'justify-between': alignment == 'between',
          'bg-neutral-150': !image?.asset,
        }
      )}
    >
      {content && (
        <div
          className={clsx('last:*:mb-0', {
            'md:w-11/12 lg:w-10/12 xl:w-3/4':
              reduceContentWidth && image?.asset,
            'mb-6': button && buttonType !== 'hide',
            'text-white': image?.asset,
          })}
        >
          <ContentEditor content={content} />
        </div>
      )}
      {button && buttonType !== 'hide' && buttonType !== 'none' && (
        <div>
          <Button
            {...button}
            mimic={buttonType == 'wrap' ? true : false}
          />
        </div>
      )}
    </div>
    {mediaType == 'video' && video ? (
      <div
        className={clsx(
          'left-0 top-0 h-full w-full overflow-hidden rounded-2xl',
          {
            absolute: content,
            relative: !content,
          }
        )}
      >
        <Video
          videoType={'file'}
          file={video}
          mobileFile={mobileVideo}
          autoplay
          mobileAutoplay
        />
      </div>
    ) : mediaType == 'oembed' && oembed ? (
      <div
        className={clsx(
          'left-0 top-0 h-full w-full overflow-hidden rounded-2xl',
          {
            absolute: content,
            relative: !content,
          }
        )}
      >
        <Video
          videoType={'oembed'}
          oembed={oembed}
          mobileOembed={mobileOembed}
          autoplay
          mobileAutoplay
        />
      </div>
    ) : mediaType == 'image' && image?.asset ? (
      <Picture
        image={image}
        imageMobile={mobileImage}
        classNamePic={clsx(
          'absolute left-0 top-0 z-[1] size-full overflow-hidden rounded-2xl',
          { 'bg-black/30': imageOverlay }
        )}
        classNameImg={clsx('size-full object-cover', {
          'mix-blend-multiply': imageOverlay,
        })}
      />
    ) : null}
  </>
)

// Main export
const BannerDefault = ({
  mediaType,
  content,
  image,
  mobileImage,
  imageSize,
  video,
  mobileVideo,
  button,
  buttonType,
  alignment = 'middle',
  aspectRatio = 'full',
  reduceContentWidth = false,
  imageOverlay,
  oembed,
  mobileOembed,
}: Props) => {
  const classes = clsx('relative block overflow-hidden rounded-2xl md:h-full', {
    'aspect-[6/5] md:aspect-[7/5]': aspectRatio == 'full' || aspectRatio == 'half',
    'group/wrapper': buttonType !== 'inline',
  })

  return buttonType == 'inline' || buttonType == 'none' ? (
    <div className={classes}>
      <BannerInner
        mediaType={mediaType}
        video={video}
        mobileVideo={mobileVideo}
        content={content}
        image={image}
        mobileImage={mobileImage}
        imageSize={imageSize}
        button={button}
        buttonType={buttonType}
        alignment={alignment}
        aspectRatio={aspectRatio}
        reduceContentWidth={reduceContentWidth}
        oembed={oembed}
        mobileOembed={mobileOembed}
        imageOverlay={imageOverlay}
      />
    </div>
  ) : (
    <Link
      to={getLinkType({
        linkType: button?.linkType,
        linkReference: button?.linkReference,
        linkSlug: button?.linkSlug,
        linkExternal: button?.linkExternal,
      })}
      className={classes}
    >
      <BannerInner
        mediaType={mediaType}
        video={video}
        mobileVideo={mobileVideo}
        content={content}
        image={image}
        mobileImage={mobileImage}
        imageSize={imageSize}
        button={button}
        buttonType={buttonType}
        alignment={alignment}
        aspectRatio={aspectRatio}
        reduceContentWidth={reduceContentWidth}
        oembed={oembed}
        mobileOembed={mobileOembed}
        imageOverlay={imageOverlay}
      />
    </Link>
  )
}

export default BannerDefault
